/**
 * Контракты приглашений и настроек почты (docs/05 §3, §3a; UC-USR-06, UC-ADM-01).
 */

import { z } from 'zod';

import { GlobalRoleSchema, IsoDateTimeSchema, UuidSchema } from './common.js';

/** Способы защиты SMTP-соединения: SMTPS и STARTTLS (docs/05 §3a). */
export const MailSecureSchema = z.enum(['tls', 'starttls']);
export type MailSecure = z.infer<typeof MailSecureSchema>;

export const MailSettingsSchema = z.object({
  isEnabled: z.boolean(),
  host: z.string().nullable(),
  port: z.number().int().min(1).max(65535),
  secure: MailSecureSchema,
  username: z.string().nullable(),
  /** Пароль наружу не отдаётся — только факт его наличия (UC-ADM-01 AC1). */
  hasPassword: z.boolean(),
  fromEmail: z.string().nullable(),
  fromName: z.string().nullable(),
  /** Производное: можно ли отправлять письма (docs/03 §1). */
  isConfigured: z.boolean(),
  /** Почему именно нельзя — для подсказки в UI. */
  problems: z.array(z.string()),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type MailSettings = z.infer<typeof MailSettingsSchema>;

export const UpdateMailSettingsBodySchema = z
  .object({
    isEnabled: z.boolean().optional(),
    host: z.string().min(1, 'Введите SMTP-сервер').max(255).nullable().optional(),
    port: z.coerce.number().int().min(1).max(65535).optional(),
    secure: MailSecureSchema.optional(),
    username: z.string().max(255).nullable().optional(),
    /**
     * Пустая строка/отсутствие — «оставить прежний пароль» (UC-ADM-01 AC4).
     */
    password: z.string().max(500).nullable().optional(),
    fromEmail: z.string().email('Некорректный email').max(255).nullable().optional(),
    fromName: z.string().max(120).nullable().optional(),
  })
  .strict();
export type UpdateMailSettingsBody = z.infer<typeof UpdateMailSettingsBodySchema>;

export const TestMailBodySchema = z
  .object({
    /** По умолчанию письмо уходит на email самого администратора. */
    to: z.string().email('Некорректный email').optional(),
    /** Необязательные параметры для проверки до сохранения (docs/05 §3a). */
    settings: UpdateMailSettingsBodySchema.optional(),
  })
  .strict();
export type TestMailBody = z.infer<typeof TestMailBodySchema>;

export const TestMailResponseSchema = z.object({
  to: z.string().email(),
  sentAt: IsoDateTimeSchema,
});
export type TestMailResponse = z.infer<typeof TestMailResponseSchema>;

/** Статус приглашения (docs/05 §3). */
export const InvitationStatusSchema = z.enum(['pending', 'expired', 'accepted', 'revoked']);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

export const InvitationSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  email: z.string().email(),
  displayName: z.string(),
  role: GlobalRoleSchema,
  status: InvitationStatusSchema,
  expiresAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const CreateInvitationBodySchema = z
  .object({
    email: z.string().email('Некорректный email').max(255),
    displayName: z.string().min(1, 'Введите имя').max(80),
    role: GlobalRoleSchema.default('member'),
  })
  .strict();
export type CreateInvitationBody = z.infer<typeof CreateInvitationBodySchema>;

export const ListInvitationsQuerySchema = z.object({
  status: InvitationStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListInvitationsQuery = z.infer<typeof ListInvitationsQuerySchema>;

/** Публичный просмотр приглашения формой регистрации (UC-AUTH-09 AC1). */
export const InvitationPreviewSchema = z.object({
  email: z.string().email(),
  displayName: z.string(),
  expiresAt: IsoDateTimeSchema,
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

export const RegisterBodySchema = z
  .object({
    token: z.string().min(10, 'Некорректная ссылка').max(500),
    password: z.string().min(8, 'Пароль не короче 8 символов').max(200),
  })
  .strict();
export type RegisterBody = z.infer<typeof RegisterBodySchema>;
